
import {
  defineComponent,
  computed,
  ComputedRef,
  Ref,
  ref,
  onMounted,
} from 'vue';

import { useStore } from '@/store';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InlineMessage from 'primevue/inlinemessage';
import { hideElementsModal, TemplateEditorState as state } from '@/composables/template-editor/TemplateEditor';
import LoadElements from '@/components/template-builder/LoadElements.vue';
import { useI18n } from 'vue-i18n';
import { ElementPartTypeEnum, LoadSectionPayload, TemplateStructureEnum } from '@/types';
import { getElementFromIframe, getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { TEMPLATE_PART_ELEMENT_PART_TYPE, TEMPLATE_SYNC_ELEMENT_IDENTIFIER } from '../template-builder/utils/constants';

export default defineComponent({
  name: 'LoadElementsModal',

  components: {
    LoadElements,
    Dialog,
    Button,
    InlineMessage,
  },

  emits: ['insert-element'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const displayModal: ComputedRef<boolean> = computed(() => state.isLoadElementsModalVisible);
    const elementsTypes = computed(() => state.loadElementsType as string[]);
    const elementPartType = computed(() => state.additionalLoadElementsParams?.elementPartType || '');
    const elementSubtype = computed(() => state.additionalLoadElementsParams?.elementSubtype || '');
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const fromSyncElement = computed(() => {
      if (state.additionalLoadElementsParams && state.additionalLoadElementsParams.fromSyncElement) {
        if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && elementSubtype.value) {
          if (selectedStructure.value && selectedStructure.value.identifier !== selectedSyncElement.value.elementId) {
            const selectedStructureElement = getElementFromIframe(`#${selectedStructure.value.identifier}`);
            if (selectedStructureElement && selectedStructureElement.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)) {
              return true;
            }
          }
          return false;
        }
        return true;
      }
      return false;
    });
    const payload: Ref<LoadSectionPayload> = ref({ position: 'top', selector: '#spm_body .spm_section:first-child' });

    const headerTitle = computed(() => {
      if (elementsTypes.value.length === 1 && elementsTypes.value[0] === TemplateStructureEnum.DESIGN) {
        return 'templateBuilder.modals.loadElements.header.loadDesign';
      }
      if (elementPartType.value === ElementPartTypeEnum.STYLES) {
        return 'templateBuilder.modals.loadElements.header.loadStyle';
      }
      return 'templateBuilder.modals.loadElements.header.loadElement';
    });

    const loadActionTitle = computed(() => {
      if (elementsTypes.value.length === 1 && elementsTypes.value[0] === TemplateStructureEnum.DESIGN) {
        return t('templateBuilder.modals.loadElements.actions.chooseDesign');
      }
      if (elementPartType.value === ElementPartTypeEnum.STYLES) {
        return t('templateBuilder.modals.loadElements.actions.chooseStyle');
      }
      return t('templateBuilder.modals.loadElements.actions.chooseElement');
    });

    const inlineMessage = computed(() => {
      if (elementPartType.value === ElementPartTypeEnum.STYLES) {
        if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && elementSubtype.value) {
          if (selectedStructure.value && selectedStructure.value.identifier !== selectedSyncElement.value.elementId) {
            const selectedStructureElement = getElementFromIframe(`#${selectedStructure.value.identifier}`);
            if (selectedStructureElement
              && selectedStructureElement.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)
              && selectedStructureElement.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
            ) {
              return t('templateBuilder.modals.loadElements.syncElementWarning.styleInStyle');
            }
          }
        }
        return t('templateBuilder.modals.loadElements.syncElementWarning.style');
      }
      return t('templateBuilder.modals.loadElements.syncElementWarning.element');
    });

    const handleClose = () => {
      hideElementsModal();
    };

    const handleInsertElement = () => {
      hideElementsModal();
      emit('insert-element');
    };

    onMounted(() => {
      // We search for placeholder in the template
      const template = getTemplateIframeDocument();
      const placeholder = template?.querySelector('.sortable-group-section.sortable-placeholder') as HTMLElement;

      if (placeholder) {
        // We define the new payload
        payload.value = { position: 'bottom', selector: '.sortable-group-section.sortable-placeholder' };
      }
    });

    return {
      t,
      displayModal,
      payload,
      elementsTypes,
      headerTitle,
      loadActionTitle,
      inlineMessage,
      elementPartType,
      elementSubtype,
      fromSyncElement,
      handleClose,
      handleInsertElement,
    };
  },
});
